// Explore-stats page: QT in numbers (#205).
// Flagship Question Time overview page. Surfaces a scorecard, per-sitting-day
// volume, cumulative curve, chamber split, length-trend (avg Q and A chars
// over time), and the top-10 longest answers. Sources the `house_questions`
// ES index via the backend `/api/explore/stats/qt-overview` endpoint.

import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Grid, Box, Card, CardContent, Stack,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatBar, StatLine } from './charts';

// Small KPI card — used four-up across the top of the page.
function StatCard({ label, value, sub }) {
  return (
    <Card variant="outlined" sx={{ height: '100%', borderRadius: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">{label}</Typography>
        <Typography variant="h4" sx={{ mt: 0.5, lineHeight: 1.1 }}>
          {value ?? '—'}
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.secondary">
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function QtOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getExploreStat('qt-overview');
        if (active) setData(res);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const c = data?.charts || {};
  const score = c.scorecard || {};

  return (
    <ExploreStatsPage
      title="QT in numbers"
      description="Question Time at a glance — how many questions are asked, who answers, how long the answers run, and how interjection-heavy the period has been. Sourced live from captured HouseLive caption transcripts of every QT question."
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="qt-overview"
    >
      {data && (
        <Box>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <StatCard label="Questions captured"
                        value={score.total}
                        sub={`over ${score.sitting_days || 0} sitting day${
                          score.sitting_days === 1 ? '' : 's'}`} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard label="This week"
                        value={score.this_week}
                        sub="questions in the latest sitting week" />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard label="PM-Q share"
                        value={score.pm_share_pct != null
                          ? `${score.pm_share_pct}%` : '—'}
                        sub="of questions answered by the Prime Minister" />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard label="Avg answer length"
                        value={score.avg_answer_chars
                          ? `${score.avg_answer_chars.toLocaleString()} chars`
                          : '—'}
                        sub={score.longest_answer_chars
                          ? `longest: ${score.longest_answer_chars.toLocaleString()} chars`
                          : null} />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Questions per sitting day
                </Typography>
                <StatBar data={c.per_day || []} xKey="date" yKey="Questions"
                         height={320} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Cumulative questions to date
                </Typography>
                <StatLine data={c.cumulative || []} xKey="date"
                          keys={['Questions']} height={320} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Average question / answer length (chars)
                </Typography>
                <StatLine data={c.length_trend || []} xKey="date"
                          keys={['avg_q_chars', 'avg_a_chars']} height={320} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Chamber split
                </Typography>
                <StatBar data={c.per_chamber || []} xKey="chamber" yKey="count"
                         height={320} layout="vertical" />
                <Typography variant="caption" color="text.secondary">
                  Federation Chamber QT is not yet captured — House-only today.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Longest answers
                </Typography>
                <Stack spacing={1}>
                  {(c.longest || []).map((q, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 2,
                                       borderBottom: '1px solid',
                                       borderColor: 'divider', pb: 1 }}>
                      <Box sx={{ minWidth: 110, fontVariantNumeric: 'tabular-nums' }}>
                        {q.Date}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2">
                          {q.Asker} → {q.Responder}{' '}
                          <Typography component="span" variant="caption"
                                       color="text.secondary">
                            ({q.Portfolio})
                          </Typography>
                        </Typography>
                      </Box>
                      <Box sx={{ minWidth: 100, textAlign: 'right' }}>
                        {q['Answer chars']?.toLocaleString()} chars
                      </Box>
                    </Box>
                  ))}
                </Stack>
                <Typography variant="caption" color="text.secondary"
                            sx={{ display: 'block', mt: 1 }}>
                  Browse the full transcripts on the{' '}
                  <RouterLink to="/house-questions">
                    House Questions (QT)
                  </RouterLink>{' '}
                  search page.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
    </ExploreStatsPage>
  );
}
